import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Trophy, Ticket, Wallet as WalletIcon, Star, Wrench, Menu, X, LogOut, User as UserIcon, ShieldAlert, Crown, Facebook, Instagram, Twitter, Youtube, Music2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSiteConfig } from "../context/SiteConfigContext";
import { NeonButton } from "./Neon";
import { AIChatWidget } from "./AIChatWidget";
import { gbp } from "../lib/format";

const Brand = () => (
  <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
    <Crown className="h-6 w-6 text-[var(--neon-pink)]" />
    <span className="font-display uppercase tracking-[0.06em] text-xl sm:text-2xl leading-none">
      <span className="glow-pink">Richi<span className="brand-pound">£</span> Rich</span> <span className="text-white">Competitions</span>
    </span>
  </Link>
);

const desktopLinks = [
  { to: "/", label: "Compete" },
  { to: "/competitions", label: "Competitions" },
  { to: "/spin", label: "Daily Spin" },
  { to: "/winners", label: "Winners" },
  { to: "/members", label: "Members Club" },
];

export const Layout = () => {
  const { isAuthed, isAdmin, isVerified, user, logout } = useAuth();
  const { maintenanceMode, socialLinks } = useSiteConfig();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const doLogout = () => { logout(); setMenuOpen(false); navigate("/"); };

  const tabs = [
    { to: "/", label: "Compete", icon: Trophy, end: true },
    { to: "/my-tickets", label: "My Tickets", icon: Ticket },
    { to: "/wallet", label: "Wallet", icon: WalletIcon },
    { to: "/members", label: "Members", icon: Star },
    ...(isAdmin ? [{ to: "/admin", label: "Admin", icon: Wrench }] : []),
  ];

  return (
    <div className="min-h-screen bg-black text-foreground flex flex-col">
      {/* Top nav */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/85 backdrop-blur supports-[backdrop-filter]:bg-black/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Brand />
          <nav className="hidden md:flex items-center gap-7">
            {desktopLinks.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `font-display uppercase tracking-[0.18em] text-sm transition-colors ${isActive ? "glow-pink" : "text-[var(--text-muted)] hover:text-white"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            {isAuthed ? (
              <>
                <Link to="/wallet" data-testid="nav-wallet-pill" className="rounded-full border border-[rgba(0,255,163,.4)] px-3 py-1.5 text-sm text-[var(--neon-green)] hover:bg-[rgba(0,255,163,.08)]">
                  {gbp(user?.wallet_balance)}
                </Link>
                <div className="relative">
                  <button data-testid="profile-menu-button" onClick={() => setMenuOpen((o) => !o)} className="flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 text-sm text-white hover:border-[var(--neon-pink)]">
                    <UserIcon className="h-4 w-4" /> {user?.full_name?.split(" ")[0] || "Account"}
                  </button>
                  {menuOpen && (
                    <div className="absolute right-0 mt-2 w-52 rounded-xl border border-white/10 bg-[var(--surface)] p-1 shadow-2xl" onMouseLeave={() => setMenuOpen(false)}>
                      {[{ to: "/my-tickets", label: "My Tickets" }, { to: "/wallet", label: "Wallet" }, { to: "/members", label: "Members Club" }, { to: "/profile", label: "Profile & Verification" }, ...(isAdmin ? [{ to: "/admin", label: "Admin Dashboard" }] : [])].map((i) => (
                        <Link key={i.to} to={i.to} onClick={() => setMenuOpen(false)} className="block rounded-lg px-3 py-2 text-sm text-[var(--text-muted)] hover:bg-white/5 hover:text-white">{i.label}</Link>
                      ))}
                      <button data-testid="logout-button" onClick={doLogout} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-[var(--neon-pink)] hover:bg-white/5"><LogOut className="h-4 w-4" /> Log out</button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <NeonButton variant="ghost" as={Link} to="/login" data-testid="nav-login" className="px-4 py-2">Login</NeonButton>
                <NeonButton variant="pink" as={Link} to="/register" data-testid="nav-register" className="px-4 py-2">Join Now</NeonButton>
              </>
            )}
          </div>
          <button className="md:hidden text-white" data-testid="mobile-menu-button" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu">
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-white/10 bg-black px-4 py-3 space-y-1">
            {desktopLinks.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)} className="block py-2 font-display uppercase tracking-widest text-[var(--text-muted)]">{l.label}</Link>
            ))}
            {isAuthed ? (
              <>
                <Link to="/profile" onClick={() => setMenuOpen(false)} className="block py-2 font-display uppercase tracking-widest text-[var(--text-muted)]">Profile</Link>
                <button onClick={doLogout} className="block py-2 font-display uppercase tracking-widest text-[var(--neon-pink)]">Log out</button>
              </>
            ) : (
              <div className="flex gap-3 pt-2">
                <NeonButton variant="outlineCyan" as={Link} to="/login" onClick={() => setMenuOpen(false)} className="flex-1">Login</NeonButton>
                <NeonButton variant="pink" as={Link} to="/register" onClick={() => setMenuOpen(false)} className="flex-1">Join</NeonButton>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Maintenance banner */}
      {maintenanceMode && (
        <div data-testid="maintenance-banner" className="bg-[rgba(255,184,0,.12)] border-b border-[rgba(255,184,0,.5)] text-center py-2.5 px-4 text-sm text-[var(--neon-gold)]">
          <Wrench className="inline h-4 w-4 mr-1.5" />
          The site is currently under maintenance — you can keep browsing, but ticket sales and new sign-ups are paused. Please check back soon.
        </div>
      )}

      {/* Verification banner */}
      {isAuthed && !isVerified && (
        <Link to="/profile" data-testid="verify-banner" className="block bg-[rgba(255,45,149,.12)] border-b border-[rgba(255,45,149,.4)] text-center py-2 text-sm text-[var(--neon-pink)] hover:bg-[rgba(255,45,149,.18)]">
          <ShieldAlert className="inline h-4 w-4 mr-1" /> Verify your email &amp; phone to enter competitions — tap here
        </Link>
      )}

      <main className="flex-1 pb-24 md:pb-10"><Outlet /></main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-black">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Brand />
            <p className="text-xs text-[var(--text-muted)] text-center sm:text-right max-w-md">
              18+ only. Please gamble responsibly. Need support? Visit <a href="https://www.begambleaware.org" target="_blank" rel="noreferrer" className="text-[var(--neon-cyan)] underline">BeGambleAware.org</a> or call 0808 8020 133. Skill-based prize competitions with free postal entry route.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 border-t border-white/10 pt-5">
            {[
              { to: "/legal/how-to-play", label: "How To Play" },
              { to: "/legal/terms", label: "Terms" },
              { to: "/legal/privacy", label: "Privacy" },
              { to: "/legal/cookies", label: "Cookies" },
              { to: "/legal/free-entry", label: "Free Entry" },
              { to: "/legal/responsible-gambling", label: "Responsible Play" },
              { to: "/legal/contact", label: "Contact" },
              { to: "/legal", label: "All Legal" },
            ].map((l) => (
              <Link key={l.to} to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-xs uppercase tracking-wider text-[var(--text-muted)] hover:text-[var(--neon-cyan)]">{l.label}</Link>
            ))}
          </div>
          {(() => {
            const SOCIALS = [
              { key: "facebook", Icon: Facebook, label: "Facebook" },
              { key: "instagram", Icon: Instagram, label: "Instagram" },
              { key: "tiktok", Icon: Music2, label: "TikTok" },
              { key: "twitter", Icon: Twitter, label: "X" },
              { key: "youtube", Icon: Youtube, label: "YouTube" },
            ].filter((s) => socialLinks && socialLinks[s.key]);
            return SOCIALS.length ? (
              <div className="flex items-center justify-center gap-4" data-testid="footer-social-links">
                {SOCIALS.map(({ key, Icon, label }) => (
                  <a key={key} href={socialLinks[key]} target="_blank" rel="noreferrer" aria-label={label} data-testid={`social-${key}`}
                    className="text-[var(--text-muted)] transition-colors hover:text-[var(--neon-pink)]">
                    <Icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            ) : null;
          })()}
        </div>
        {/* Persistent compliance strip (every page) */}
        <div data-testid="compliance-strip" className="border-t border-white/10 px-4 py-2 text-center text-[10px] leading-tight text-[var(--text-muted)]">
          18+ only · No purchase necessary — free postal entry available · Please gamble responsibly · BeGambleAware.org
        </div>
      </footer>

      {/* Mobile bottom tabs */}
      <nav data-testid="mobile-bottom-tabbar" className="md:hidden fixed bottom-0 inset-x-0 z-50 border-t border-white/10 bg-black/95 backdrop-blur">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
          {tabs.map((tab) => (
            <NavLink key={tab.to} to={tab.to} end={tab.end} data-testid={`tab-${tab.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] uppercase tracking-wider ${isActive ? "text-[var(--neon-pink)]" : "text-[var(--text-muted)]"}`}>
              {({ isActive }) => (<><tab.icon className={`h-5 w-5 ${isActive ? "drop-shadow-[0_0_6px_rgba(255,45,149,.8)]" : ""}`} /><span>{tab.label}</span></>)}
            </NavLink>
          ))}
        </div>
      </nav>

      <AIChatWidget />
    </div>
  );
};
